import styled from '@emotion/styled';

import { Button } from '@travelnest/hui';

import {
	type CancellationPreferenceTypes,
	freeCancellationDetails,
} from '@/lib/cancellationPolicy';
import { formatMoney } from '@/lib/currency';
import { formatStayDates, hasSelectedStay, totalGuests } from '@/lib/stay';
import {
	colors,
	focusRing,
	font,
	mq,
	pageGutter,
	radii,
	spacing,
	zIndex,
} from '@/lib/theme';
import { useCurrentLocale, useI18n } from '@/locales';

import type { StayPricing } from './useStayPricing';

const Bar = styled.div`
	position: fixed;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: ${zIndex.stickyBar};
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${spacing.space40};
	padding: ${spacing.space40} ${pageGutter.mobile};
	padding-bottom: calc(${spacing.space40} + env(safe-area-inset-bottom));
	background: ${colors.WH100};
	border-top: 1px solid ${colors.interface.border};
	box-shadow: 0 -4px 16px rgba(15, 23, 42, 0.06);

	${mq.lg} {
		display: none;
	}

	/* A big, easy target, like the booking sites guests know. */
	& > button:last-of-type {
		flex-shrink: 0;
		min-width: 140px;
	}
`;

const Summary = styled.button`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	justify-content: center;
	gap: ${spacing.space10};
	/* Room for the total, the dates and the tag, so the bar keeps one height. */
	min-height: 76px;
	min-width: 0;
	padding: 0;
	border: 0;
	background: none;
	text-align: left;
	cursor: pointer;

	&:focus-visible {
		${focusRing}
	}
`;

const Headline = styled.span<{ $underline?: boolean }>`
	${font('IB110', '125%')}
	color: ${colors.typography.heading};
	text-decoration: ${({ $underline }) => ($underline ? 'underline' : 'none')};
	text-underline-offset: 3px;
`;

const Detail = styled.span<{ $underline?: boolean }>`
	${font('IR90')}
	color: ${colors.typography.body};
	text-decoration: ${({ $underline }) => ($underline ? 'underline' : 'none')};
	text-underline-offset: 2px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	max-width: 100%;
`;

const Tag = styled.span`
	padding: 2px ${spacing.space20};
	border-radius: ${radii.radii20};
	background: ${colors.LM_NU20};
	${font('IR80')}
	color: ${colors.typography.body};
	white-space: nowrap;
`;

interface BookingBarProps {
	pricing: StayPricing;
	cancellationPolicy: CancellationPreferenceTypes | string;
	onOpenSheet: () => void;
	onOpenCalendar: () => void;
	onReserve: () => void;
}

/**
 * The fixed bar at the bottom of the property page on phones. Reserve opens
 * the calendar until dates are chosen; once priced, the bar shows the total
 * and Reserve reserves. Tapping the summary opens the full booking sheet.
 */
export const BookingBar = ({
	pricing,
	cancellationPolicy,
	onOpenSheet,
	onOpenCalendar,
	onReserve,
}: BookingBarProps) => {
	const t = useI18n();
	const locale = useCurrentLocale();
	const { stay, currency, discountExceedsTotal } = pricing;
	const selected = hasSelectedStay(stay);
	const total = discountExceedsTotal
		? (stay.totalBeforeDiscount ?? 0)
		: (stay.totalToPayToday ?? 0);
	const guests = totalGuests(stay);
	const guestCount =
		guests === 1
			? t('booking.summary.guest')
			: t('booking.summary.guests', { count: guests });
	const freeCancellation =
		selected &&
		stay.startDate &&
		freeCancellationDetails(
			cancellationPolicy as CancellationPreferenceTypes,
			stay.startDate,
		)?.isRefundable;

	return (
		<Bar data-testid="booking-bar">
			<Summary type="button" onClick={onOpenSheet}>
				{selected ? (
					<>
						<Headline $underline>
							{t('booking.bar.total', {
								amount: formatMoney(total, currency),
							})}
						</Headline>
						<Detail>
							{formatStayDates(
								stay.startDate,
								stay.endDate,
								locale,
							)}
							{' · '}
							{guestCount}
						</Detail>
						{freeCancellation && (
							<Tag>{t('booking.bar.freeCancellation')}</Tag>
						)}
					</>
				) : (
					<>
						<Headline>{t('booking.addDatesForPrice')}</Headline>
						<Detail $underline>{guestCount}</Detail>
					</>
				)}
			</Summary>
			<Button
				text={t('booking.reserve')}
				variant="primary"
				size="large"
				disabled={discountExceedsTotal}
				onClick={selected ? onReserve : onOpenCalendar}
			/>
		</Bar>
	);
};
